import { useEffect, useMemo, useState } from "react";
import { Typography, Spin, Card, Progress } from "antd";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  getProyectosInformeTecnicoFinal,
  obtenerEspacioDrive
} from "../../services/informeTecnicoFinal/informeTecnicoFinalService";
import ProyectosFilters from "./components/ProyectosFilters";
import ProyectosCards from "./components/ProyectosCards";
import ProyectosTable from "./components/ProyectosTable";
import InformeTecnicoFinalModal from "./components/InformeTecnicoFinalModal";

const { Title, Text } = Typography;
const NAVY = "#042956";

function formatGB(bytes) {
  if (bytes == null) return "—";
  return (bytes / 1024 ** 3).toFixed(1);
}

export default function InformeTecnicoFinalPage() {
  const appName = "informe_tecnico_final";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionAccess = permissions.priv_access === "Y";

  // App aparte: no todos los usuarios con acceso al módulo deben ver cuánto
  // espacio de Drive queda disponible.
  const espacioPermissions = useSelector((state) =>
    selectAppPermissions(state, "informe_tecnico_final_espacio_drive")
  );
  const permissionVerEspacio = espacioPermissions.priv_access === "Y";
  const [espacioDrive, setEspacioDrive] = useState(null);

  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const cargarProyectos = async () => {
    setLoading(true);
    const { data } = await getProyectosInformeTecnicoFinal();
    setProyectos(data);
    setLoading(false);
  };

  useEffect(() => {
    if (permissionAccess) cargarProyectos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionAccess]);

  useEffect(() => {
    if (!permissionVerEspacio) return;
    obtenerEspacioDrive().then(({ data }) => {
      if (data) setEspacioDrive(data);
    });
  }, [permissionVerEspacio]);

  const estadosDisponibles = useMemo(
    () => [...new Set(proyectos.map((p) => p.ac_estado).filter(Boolean))],
    [proyectos]
  );

  const proyectosFiltrados = useMemo(() => {
    const term = search.trim().toLowerCase();

    return proyectos.filter((p) => {
      const matchSearch =
        !term ||
        p.codigo?.toLowerCase().includes(term) ||
        p.proyecto?.toLowerCase().includes(term) ||
        p.Nombre_proyectoAsociado?.toLowerCase().includes(term);

      const matchEstado = !estado || p.ac_estado === estado;

      return matchSearch && matchEstado;
    });
  }, [proyectos, search, estado]);

  const handleSelect = (proyecto) => {
    setSelectedProyecto(proyecto);
    setModalVisible(true);
  };

  const handleReset = () => {
    setSearch("");
    setEstado(null);
  };

  if (!permissionAccess) {
    return <div className="container">No tiene permisos para acceder a este módulo</div>;
  }

  return (
    <div className="container">

      <div className="row mb-2 d-flex align-items-center">
        <div className="col-md-8 linea_separador mb-2 d-flex align-items-center">
          <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
            <p>PROYECTO UTF <br /> COL 160 COL</p>
          </div>
          <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
            <p>Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET</p>
          </div>
        </div>
        <div className="col-md-4 d-flex justify-content-center align-items-center flex-column">
          <Title level={3} style={{ color: "#042956", marginBottom: 4 }}>
            Informe Técnico Final
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Consulta y descarga la documentación de cierre de cada proyecto almacenada en Google Drive
          </Text> 
          {permissionVerEspacio && espacioDrive && (
        <Card
          size="small"
          style={{ marginBottom: 20, borderRadius: 12, border: "1px solid #eef1f5", maxWidth: 450, padding:10 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <Text style={{ fontSize: 10, color: "#5b6b7c", fontWeight: 500, paddingLeft: 4, paddingRight: 4 }}>Google Drive</Text>
            <Text style={{ fontSize: 10, color: "#5b6b7c", paddingLeft: 4, paddingRight: 4 }}>
              {formatGB(espacioDrive.usageBytes)} GB de {formatGB(espacioDrive.limitBytes)} GB
            </Text>
             <Text style={{ fontSize: 10, color: "#8c9bab", paddingLeft: 4, paddingRight: 4 }}>
            {espacioDrive.porcentajeUsado}% usado · {formatGB(espacioDrive.limitBytes - espacioDrive.usageBytes)} GB libres
          </Text>
          </div>
          <Progress
            percent={espacioDrive.porcentajeUsado ?? 0}
            size="small"
            showInfo={false}
            strokeColor={
              espacioDrive.porcentajeUsado >= 90
                ? "#cf1322"
                : espacioDrive.porcentajeUsado >= 70
                  ? "#faad14"
                  : NAVY
            }
          />
         
        </Card>
      )}</div>  </div>



      

      <ProyectosFilters
        search={search}
        onSearchChange={setSearch}
        estado={estado}
        onEstadoChange={setEstado}
        estadosDisponibles={estadosDisponibles}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onReset={handleReset}
      />

      {loading && proyectos.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : viewMode === "cards" ? (
        <ProyectosCards proyectos={proyectosFiltrados} onSelect={handleSelect} />
      ) : (
        <ProyectosTable proyectos={proyectosFiltrados} loading={loading} onSelect={handleSelect} />
      )}

      <InformeTecnicoFinalModal
        visible={modalVisible}
        proyecto={selectedProyecto?.codigo}
        nombreProyecto={selectedProyecto?.proyecto}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
}
